import * as V1 from "kmmp/orders/v1";
import * as Frontend from "kmmp/frontend";
import { action, autorun, computed, makeObservable, observable, toJS } from "mobx";
import { CONSTANTS } from "client/constants";
import { Dev } from ".";
import { uuid } from "shared/uuid";
import { Rate } from "shipping-ups";
import { mapToFrontendImage } from "shared/map_images";
import { mapStringToFrame } from "shared/map-string-to-frame";
import { hasOnlySampleItems } from "shared/has-only-sample-items";
import { ExhaustiveCheckError } from "shared/errors";
import { Sentry } from "client/modules/sentry";

type ShippingConfig = { ambiguous: true } | { ambiguous: false; rates: Rate[]; saturdayDeliveryAvailable: boolean };
type StoredData = V1.LineItem[] | { version: "2"; items: ReadonlyArray<Frontend.LineItem> };

/**
 * Safely reads from localStorage with error handling and Sentry monitoring.
 * Returns null if the operation fails.
 */
function safeLocalStorageGet(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch (error) {
        const sourceId = "d8f3c1e4-5b2a-4f9c-8e1d-7a6b9c3f2e4a";

        Sentry.addBreadcrumb({
            message: "Failed to read from localStorage",
            category: "Kmmp.Browser.Cart.LocalStorage",
            level: "error",
            data: { key },
        });
        Sentry.captureException(error, {
            level: "error",
            extra: {
                category: "Kmmp.Browser.Cart.LocalStorage.Read",
                sourceId: sourceId,
                key: key,
            },
        });

        console.error(`Failed to read from localStorage (key: ${key}, sourceId: ${sourceId}):`, error);
        return null;
    }
}

/**
 * Safely writes to localStorage with error handling and Sentry monitoring.
 * Returns true if successful, false otherwise.
 */
function safeLocalStorageSet(key: string, value: string): boolean {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (error) {
        const sourceId = "e9a4d2f5-6c3b-4a8d-9f2e-8b7c4d5e3f6b";

        Sentry.addBreadcrumb({
            message: "Failed to write to localStorage",
            category: "Kmmp.Browser.Cart.LocalStorage",
            level: "error",
            data: { key, valueLength: value.length },
        });
        Sentry.captureException(error, {
            level: "error",
            extra: {
                category: "Kmmp.Browser.Cart.LocalStorage.Write",
                sourceId: sourceId,
                key: key,
                valueLength: value.length,
                errorName: error instanceof Error ? error.name : "Unknown",
                errorMessage: error instanceof Error ? error.message : String(error),
            },
        });

        console.error(`Failed to write to localStorage (key: ${key}, sourceId: ${sourceId}):`, error);
        return false;
    }
}

export class CartStore {
    constructor() {
        makeObservable(this, {
            decedent_first_name: observable,
            decedent_family_name: observable,
            items: observable,
            startedCheckout: observable,
            address: observable,
            shipping: observable,
            details: observable,
            containsReorders: computed,
            setPrefersEmailReceipts: action,
            addToCart: action,
            addSamplePortraitsToCart: action,
            addReordersToCart: action,
            setQuantity: action,
            removeItem: action,
            setNames: action,
            startCheckout: action,
            setVerifiedShippingAddressAndRates: action,
            setAmbiguousAddress: action,
            setDetails: action,
            reset: action,
        });

        // Parse settings from localstorage with error handling
        const prefersEmailReceipts = safeLocalStorageGet(CONSTANTS.PREFERS_EMAIL_RECEIPTS_COOKIE_NAME);

        if (prefersEmailReceipts) {
            try {
                this.prefersEmailReceipts.set(JSON.parse(prefersEmailReceipts) === true);
            } catch (error) {
                console.error("Failed to parse prefersEmailReceipts from localStorage:", error);
                // Continue with default value (false)
            }
        }

        // Parse cart data from localstorage with error handling
        try {
            const cartDataRaw = safeLocalStorageGet(CONSTANTS.CART_COOKIE_NAME);
            const cartData = JSON.parse(cartDataRaw || "[]") as StoredData;

            if (Array.isArray(cartData)) {
                // The parsed value is an array of V1.LineItem. Map them to Frontend.LineItem.
                // V1.LineItem is always a portrait.
                this.items = cartData.map<Frontend.LineItem>((item) => ({
                    type: "portrait",
                    uuid: item.uuid,
                    deceasedName: item.deceasedName,
                    source: item.source,
                    purchaseOrderId: item.purchaseOrderId || null,
                    originalOrderId: item.originalOrderId || null,
                    customization: {
                        orientation: item.customization.orientation,
                        package: item.customization.package,
                        background: {
                            fileName: item.customization.background.image,
                            name: item.customization.background.name,
                            notes: item.customization.background.notes,
                        },
                        frame: {
                            fileName: item.customization.frame.portraitImage,
                            name: mapStringToFrame(item.customization.frame.name),
                        },
                    },
                    images: item.images.map(mapToFrontendImage),
                    qty: {
                        cards: item.quantities.extraCards,
                        prints: item.quantities.extraPrints,
                        packages: item.quantities.total,
                    },
                }));
            } else {
                this.items = cartData.items;
            }
        } catch (error) {
            const sourceId = "f1a5b3d7-8c4e-4b9a-9e2f-3d6c7e8a9b1c";

            Sentry.addBreadcrumb({
                message: "Failed to parse cart data from localStorage",
                category: "Kmmp.Browser.Cart.LocalStorage",
                level: "error",
            });
            Sentry.captureException(error, {
                level: "error",
                extra: {
                    category: "Kmmp.Browser.Cart.LocalStorage.Parse",
                    sourceId: sourceId,
                },
            });

            console.error(`Failed to parse cart data from localStorage (sourceId: ${sourceId}):`, error);
            // Continue with empty cart
            this.items = [];
        }

        // Autorun to persist cart state to localStorage with error handling
        autorun((reaction) => {
            // trace() is stripped from MobX's production build — only call it in Vite dev mode
            if (import.meta.env.DEV) reaction.trace();

            // Call mobx.toJS directly on the observable array so that MobX correctly
            // deep-converts all observable proxies to plain JS before serialization.
            // Wrapping the array in a plain object first and calling toJS on that is a
            // no-op in MobX 6 because toJS only converts values for which isObservable()
            // is true — plain object literals are returned unchanged, leaving the items
            // as observable proxies that JSON.stringify cannot reliably serialize.
            const stored: StoredData = {
                version: "2",
                items: toJS(this.items),
            };

            // Attempt to save cart data
            safeLocalStorageSet(CONSTANTS.CART_COOKIE_NAME, JSON.stringify(stored));

            // Attempt to save email preference. Use .get() to read the boxed value
            // explicitly so the dependency is tracked and the value (not the box object)
            // is serialized.
            safeLocalStorageSet(
                CONSTANTS.PREFERS_EMAIL_RECEIPTS_COOKIE_NAME,
                JSON.stringify(this.prefersEmailReceipts.get())
            );
        }, {
            name: "CartStore.Serializer",
            onError: (error) => {
                Sentry.captureException(error, {
                    level: "error",
                    extra: {
                        category: "Kmmp.Browser.CartStore.Serializer.autorun",
                    },
                });
                console.error("Autorun encountered an error", error)
            },
            requiresObservable: true,
        });
    }

    decedent_first_name: string = "";

    decedent_family_name: string = "";

    items: ReadonlyArray<Frontend.LineItem> = [];

    startedCheckout = false;

    address: Frontend.ShippingAddress = {
        city: "",
        name: "",
        state: "",
        zip: "",
        country: "",
        street1: "",
        street2: "",
        street3: "",
    };

    shipping: ShippingConfig = { ambiguous: true };

    details: Frontend.Details = {
        email: "",
        needed_by_date: "",
        needed_by_time: "",
        phone: "",
        placed_by: "",
        send_confirmation: false,
    };

    /**
     * Indicates that the user prefers to receive email receipts and the option should be on by default.
     */
    prefersEmailReceipts = observable.box(false);

    get containsReorders() {
        return this.items.some((i) => !!i.originalOrderId);
    }

    get hasOnlySampleItems() {
        return hasOnlySampleItems(this.items);
    }

    setPrefersEmailReceipts = (newValue: boolean) => {
        this.prefersEmailReceipts.set(newValue);
    };

    addToCart(item: Frontend.LineItem) {
        item.uuid = item.uuid || uuid();

        if (Dev.allow_cart === false) {
            this.items = [item];
        } else {
            this.items = [...this.items, item];
        }
    }

    addSamplePortraitsToCart(item: Frontend.SampleLineItem) {
        if (Dev.allow_cart === false) {
            this.items = [item];
        } else {
            this.items = [...this.items, item];
        }
    }

    addReordersToCart(items: Frontend.LineItem[], originalOrderId: string) {
        if (items.some((i) => !i.uuid)) {
            throw new Error(
                `An item was passed to mobx Cart store .addReordersToCart that didn't have a uuid. Reordered line items should have uuids that match the original order's line item uuid.`
            );
        }

        const newItems = items.map<Frontend.LineItem>((i) => ({
            ...i,
            originalOrderId: originalOrderId,
        }));

        if (Dev.allow_cart === false) {
            this.items = newItems;
        } else {
            this.items = [...this.items, ...newItems];
        }
    }

    setQuantity(item: Frontend.LineItem, quantityType: "packages" | "cards" | "prints", quantity: number) {
        const items = [...this.items];
        const index = items.findIndex((i) => i.uuid === item.uuid);
        const updatedItem = items[index];

        switch (updatedItem.type) {
            case undefined:
            case "portrait":
                switch (quantityType) {
                    case "packages":
                        updatedItem.qty.packages = quantity;
                        break;

                    case "cards":
                        updatedItem.qty.cards = quantity;
                        break;

                    case "prints":
                        updatedItem.qty.prints = quantity;
                        break;
                }
                break;

            case "shadow-box":
            case "signature-jersey":
            case "signature-portrait":
                updatedItem.qty.packages = quantity;
                break;

            case "sample-portraits":
                throw new Error("The sample-portraits line item type cannot be updated using this function.");

            default:
                throw new ExhaustiveCheckError(updatedItem);
        }

        items.splice(index, 1, updatedItem);

        this.items = items;
    }

    removeItem(item: Frontend.LineItem) {
        const items = [...this.items];
        const index = items.findIndex((i) => i.uuid === item.uuid);

        items.splice(index, 1);

        this.items = items;
    }

    setNames(firstName: string, familyName: string) {
        this.decedent_first_name = (firstName ?? "").trim();
        this.decedent_family_name = (familyName ?? "").trim();
        this.items = this.items.map((i) => {
            if (i.type === "sample-portraits") return i;
            return {
                ...i,
                deceasedName: (this.decedent_first_name + " " + this.decedent_family_name),
            };
        });
    }

    startCheckout() {
        this.startedCheckout = true;
    }

    setVerifiedShippingAddressAndRates(address: Frontend.ShippingAddress, shipping: Frontend.ShippingRateData) {
        this.address = address;
        this.shipping = {
            ambiguous: false,
            rates: Array.isArray(shipping.rates) ? shipping.rates : [],
            saturdayDeliveryAvailable: shipping.saturdayDeliveryAvailable || false,
        };
    }

    setAmbiguousAddress(address: Frontend.ShippingAddress) {
        this.address = address;
        this.shipping = {
            ambiguous: true,
        };
    }

    setDetails(details: Frontend.Details) {
        this.details = details;
    }

    /**
     * Resets the cart, removing all items and saving the state to localStorage.
     */
    reset() {
        this.items = [];
        this.startedCheckout = false;
        this.decedent_first_name = "";
        this.decedent_family_name = "";
        this.details = {
            email: "",
            needed_by_date: "",
            needed_by_time: "",
            phone: "",
            placed_by: "",
            send_confirmation: false,
        };
        this.address = {
            city: "",
            name: "",
            state: "",
            zip: "",
            country: "",
            street1: "",
            street2: "",
            street3: "",
        };
        this.shipping = {
            ambiguous: true,
        };
    }
}

export const Cart = new CartStore();

window["CartStore"] = Cart;
